import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { map, omit } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { deeplinksApi } from 'api';
import {
  FormActions,
  FormControls,
  FormikSelect,
  FormikTextField,
} from 'components';
import { DeviceOS } from 'enums';
import { useMutation, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { CreateDeeplinkDto, Deeplink } from 'types';
import { deviceUtils, formUtils, validationUtils } from 'utils';

import { useDeeplinks } from './DeeplinksList.context';

type Values = CreateDeeplinkDto;

// mirrors the SCI renderer, which hides a deeplink whose blocks it cannot pair up
const hasBalancedUrlEncodeBlocks = (url = ''): boolean => {
  let depth = 0;
  for (const [, closing] of Array.from(url.matchAll(/\[(\/?)URL_ENCODE\]/g))) {
    depth += closing ? -1 : 1;
    if (depth < 0) {
      return false;
    }
  }
  return depth === 0;
};

type Props = {
  onSubmit?: (values: Deeplink) => void;
  selectedDeeplink?: Deeplink | null;
};

export const DeeplinksDetailsForm: React.FC<Props> = ({
  onSubmit,
  selectedDeeplink = null,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.deeplinks',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { tradeMethod, deeplinksQuery } = useDeeplinks();
  const { banks } = useUserContext();

  const initialValues: Values = useMemo(
    () => ({
      customerBank: selectedDeeplink?.customerBank || '',
      url: selectedDeeplink?.url || '',
      deviceOs: selectedDeeplink?.deviceOs,
      tradeMethodId: tradeMethod?.id || selectedDeeplink?.tradeMethodId || '',
    }),
    [selectedDeeplink, tradeMethod],
  );

  const deviceOsOptions = useMemo(
    () =>
      map(DeviceOS, (deviceOs) => ({
        value: deviceOs,
        label: deviceUtils.getDeviceOsLabel(deviceOs),
      })),
    [],
  );

  const bankOptions = useMemo(
    () => formUtils.getOptions(banks, 'name', 'code'),
    [banks],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        url: Yup.string()
          .required(tCommon('errors.required'))
          .test(
            'url-encode-blocks',
            t('errors.url_encode_blocks'),
            hasBalancedUrlEncodeBlocks,
          ),
        tradeMethodId: Yup.string().required(tCommon('errors.required')),
      }),
    [t, tCommon],
  );

  const createDeeplink = useMutation<Deeplink, AxiosError, Values>(
    deeplinksApi.create,
  );

  const updateDeeplink = useMutation<Deeplink, AxiosError, Values>((deeplink) =>
    deeplinksApi.update({
      id: selectedDeeplink!.id,
      data: omit(deeplink, 'tradeMethodId'),
    }),
  );

  const doSubmit = useMemo(() => {
    if (selectedDeeplink) {
      return updateDeeplink;
    }
    return createDeeplink;
  }, [selectedDeeplink, createDeeplink, updateDeeplink]);

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      doSubmit.mutate(values, {
        onSuccess: (values) => {
          deeplinksQuery.refetch();
          onSubmit?.(values);
        },
        onError: (error: AxiosError) => {
          formikHelpers.setErrors(validationUtils.getFormErrors(error));
        },
        onSettled: () => {
          formikHelpers.setSubmitting(false);
        },
      });
    },
    [onSubmit, deeplinksQuery, doSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <FormControls>
            <FormikSelect
              name="customerBank"
              label={t('fields.customer_bank')}
              options={bankOptions}
              disablePortal={false}
            />
            <FormikTextField label={t('fields.url')} name="url" required />
            <div className="tw-text-xs tw-italic tw-text-gray-500 tw-pb-4">
              {t('fields.url_helper_text')}
            </div>
            <FormikSelect
              label={t('fields.device_os')}
              name="deviceOs"
              options={deviceOsOptions}
              noneOption
            />
          </FormControls>
          <FormActions>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={isSubmitting}
            >
              {tCommon('buttons.save')}
            </Button>
          </FormActions>
        </Form>
      )}
    </Formik>
  );
};
